// The purchased theme's CSS (Bootstrap, slick, glightbox, AOS, nice-select2)
// went with the theme pages. Every route now renders through the scoped .q
// layer in assets/scss/qaflo, which owns its own reset, grid and motion, so
// none of those stylesheets have anything left to style. Dropping them takes
// roughly 250KB of blocking CSS off first paint.
import '@/assets/scss/main.scss'
import type { GatsbyBrowser } from 'gatsby'
import { loadAnalyticsLater, trackPageView } from '@/utils/analytics'

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  loadAnalyticsLater()
}

// Comprehensive scroll restoration fix for Gatsby
// This addresses timing issues with content loading and rehydration

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({ routerProps: { location } }) => {
  // If navigating to the same page (e.g., anchor links), use browser default
  if (location.hash) {
    return true
  }

  // For all other navigation, scroll to top
  // Using requestAnimationFrame ensures it happens after DOM updates
  window.requestAnimationFrame(() => {
    window.scrollTo(0, 0)
  })

  return false
}

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location, prevLocation }) => {
  // The first load is counted by the inline `config`; only client-side moves send one here.
  if (prevLocation && location.pathname !== prevLocation.pathname) trackPageView(location.pathname)

  // Skip if it's the same page or has a hash (anchor link)
  if (location.hash || location.pathname === prevLocation?.pathname) {
    return
  }

  // Multiple scroll attempts to handle async content and rehydration
  // Immediate scroll
  window.scrollTo(0, 0)

  // Using requestAnimationFrame for better timing with browser rendering
  window.requestAnimationFrame(() => {
    window.scrollTo(0, 0)

    // Final scroll after a short delay to catch any late-loading content
    setTimeout(() => {
      window.scrollTo(0, 0)

      // Refresh GSAP ScrollTrigger after route update
      // This ensures animations work correctly on navigation
      if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
        ;(window as any).ScrollTrigger.refresh()
      }
    }, 50)
  })
}

// onInitialClientRender used to refresh GSAP's ScrollTrigger here. GSAP was removed
// with the purchased theme and is not a dependency, so the hook was waking a frame and
// a 300ms timer on every first paint to check for a global that can never exist.
